import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaBars,
  FaTachometerAlt,
  FaUsers,
  FaVideo,
  FaCog,
  FaGavel,
  FaCalendarCheck,
  FaShieldAlt,
  FaFileContract,
  FaWallet,
  FaTrophy,
  FaHandsHelping,
  FaShareSquare,
  FaHandHoldingUsd,
  FaPaperclip,
  FaUserCheck,
  FaSlidersH,
  FaBolt,
  FaChartBar,
  FaBoxOpen,
  FaUserCog,
  FaChevronDown,
  FaChevronRight,
  FaInstagram,
} from "react-icons/fa";

// Navigation is now data-driven so the layout is easy to reshuffle and the
// active-section detection can be done in one pass. Groups whose `items` array
// matches the current route auto-expand.
const NAV_GROUPS = [
  // Dashboard is intentionally not grouped — it's the top-level landing.
  {
    id: "home",
    standalone: [
      { icon: FaTachometerAlt, text: "Dashboard", link: "/" },
    ],
  },
  {
    id: "events",
    title: "Events & Content",
    items: [
      { icon: FaCalendarCheck, text: "Events", link: "/events", matchPrefix: ["/event-form", "/event-attendance"] },
      { icon: FaVideo, text: "Video Package", link: "/video-package" },
      { icon: FaPaperclip, text: "Event Attachments", link: "/event-attachments" },
    ],
  },
  {
    id: "people",
    title: "People",
    items: [
      { icon: FaUsers, text: "Participants", link: "/participants", matchPrefix: ["/participant-detail"] },
      { icon: FaHandHoldingUsd, text: "Sponsors", link: "/sponsors", matchPrefix: ["/sponsor/view"] },
      { icon: FaHandHoldingUsd, text: "Sponsor Invoices", link: "/sponsor-invoices" },
      { icon: FaUserCheck, text: "User Approvals", link: "/user-approvals" },
    ],
  },
  {
    id: "engagement",
    title: "Engagement",
    items: [
      { icon: FaWallet, text: "Wallet", link: "/wallet" },
      { icon: FaTrophy, text: "Winnings", link: "/winnings" },
      { icon: FaShareSquare, text: "Referral", link: "/referal" },
    ],
  },
  {
    id: "support",
    title: "Support",
    items: [
      { icon: FaHandsHelping, text: "FAQ's", link: "/faqs" },
      { icon: FaHandsHelping, text: "Collab Tickets", link: "/collabtickets" },
      { icon: FaHandsHelping, text: "Help & Support", link: "/help-support" },
    ],
  },
  {
    id: "automation",
    title: "Automation",
    items: [
      { icon: FaBolt, text: "Event Automation", link: "/event-automation" },
      { icon: FaUserCog, text: "Subscriptions", link: "/subscriptions" },
      { icon: FaBoxOpen, text: "Plans", link: "/subscription-plans" },
      { icon: FaChartBar, text: "Auto Analytics", link: "/automation-analytics" },
    ],
  },
  {
    id: "settings",
    title: "Settings & Legal",
    items: [
      { icon: FaSlidersH, text: "App Settings", link: "/app-settings" },
      { icon: FaInstagram, text: "Refresh IG Token", link: "/refresh-ig-token" },
      { icon: FaFileContract, text: "Privacy Policy", link: "/privacypolicy" },
      { icon: FaShieldAlt, text: "Fair Play Policy", link: "/fairpolicy" },
      { icon: FaGavel, text: "Terms & Conditions", link: "/termcondition" },
    ],
  },
];

const STORAGE_KEY = "namasvi_sidenav_open_groups";

const itemMatches = (item, pathname) => {
  if (!item?.link) return false;
  if (item.link === "/") return pathname === "/";
  if (pathname === item.link) return true;
  if (pathname.startsWith(item.link + "/")) return true;
  if (Array.isArray(item.matchPrefix)) {
    return item.matchPrefix.some(
      (p) => pathname === p || pathname.startsWith(p + "/")
    );
  }
  return false;
};

const SideNav = ({ isMobile, isCollapsed, toggleSidebar }) => {
  const location = useLocation();
  const pathname = location.pathname;

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    // Default: only open the group that contains the current route.
    const active = NAV_GROUPS.find((g) =>
      g.items?.some((it) => itemMatches(it, pathname))
    );
    const init = {};
    NAV_GROUPS.forEach((g) => {
      if (g.standalone) return;
      init[g.id] = active && active.id === g.id;
    });
    return init;
  });

  // Whenever the route changes, make sure the group containing it is open.
  useEffect(() => {
    const active = NAV_GROUPS.find((g) =>
      g.items?.some((it) => itemMatches(it, pathname))
    );
    if (active && !openGroups[active.id]) {
      setOpenGroups((prev) => ({ ...prev, [active.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Persist per-group open state so reloads don't collapse everything.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups));
    } catch {}
  }, [openGroups]);

  const handleNavClick = () => {
    if (isMobile) toggleSidebar();
  };

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // When the sidebar is collapsed to icon-only, render every item flat — there's
  // no room for group headers and the chevrons make the UI confusing.
  const flatItems = useMemo(() => {
    const out = [];
    NAV_GROUPS.forEach((g) => {
      (g.standalone || g.items || []).forEach((it) => out.push(it));
    });
    return out;
  }, []);

  return (
    <div
      className={`fixed top-0 bottom-0 left-0 h-screen z-50 bg-white text-black transition-all duration-300 ease-in-out overflow-y-auto border-r shadow-lg sidebar ${
        isCollapsed
          ? isMobile
            ? "hidden"
            : "w-[60px]"
          : "lg:w-[18vw] w-[12rem]"
      } ${isMobile && isCollapsed ? "hidden" : "block"}`}
    >
      <div className="flex items-center justify-between p-2 lg:p-4 mx-auto ">
        {!isCollapsed && (
          <label className="w-full ml-[2vw] font-bold text-[#202224] text-sm lg:text-[1.25vw]">
            <span className="text-[#4880FF]">NAMASVI-</span>Nexus
          </label>
        )}
        <button
          onClick={toggleSidebar}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <FaBars className="text-black w-4" />
        </button>
      </div>

      <div className="flex flex-col lg:space-y-1 px-[1vw] lg:mt-2 lg:mb-4">
        {isCollapsed
          ? flatItems.map((item) => (
              <NavItem
                key={item.link}
                item={item}
                isCollapsed
                isSelected={itemMatches(item, pathname)}
                onClick={handleNavClick}
              />
            ))
          : NAV_GROUPS.map((group) => {
              if (group.standalone) {
                return group.standalone.map((item) => (
                  <NavItem
                    key={item.link}
                    item={item}
                    isCollapsed={false}
                    isSelected={itemMatches(item, pathname)}
                    onClick={handleNavClick}
                  />
                ));
              }
              const isOpen = !!openGroups[group.id];
              const hasActiveChild = group.items?.some((it) =>
                itemMatches(it, pathname)
              );
              return (
                <div key={group.id} className="mt-2">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className={`w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-md transition-colors ${
                      hasActiveChild
                        ? "text-[#4880FF]"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <span>{group.title}</span>
                    {isOpen ? (
                      <FaChevronDown className="w-2.5 h-2.5" />
                    ) : (
                      <FaChevronRight className="w-2.5 h-2.5" />
                    )}
                  </button>
                  {isOpen &&
                    group.items.map((item) => (
                      <NavItem
                        key={item.link}
                        item={item}
                        isCollapsed={false}
                        isSelected={itemMatches(item, pathname)}
                        onClick={handleNavClick}
                      />
                    ))}
                </div>
              );
            })}
      </div>
    </div>
  );
};

const NavItem = ({ item, isCollapsed, isSelected, onClick }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.link}
      title={isCollapsed ? item.text : undefined}
      className={`flex items-center p-3 cursor-pointer rounded-md font-medium lg:font-medium transition-colors duration-300 ${
        isSelected ? "bg-[#4880FF] text-white" : "text-[#202224]"
      } hover:bg-[#4880FF] hover:text-white`}
      onClick={onClick}
    >
      <div className="w-8">
        <Icon />
      </div>
      {!isCollapsed && <span className="lg:ml-2 text-sm">{item.text}</span>}
    </Link>
  );
};

export default SideNav;
